import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import {
  Upload, Mic, Video, FileText, Bot, Music, Eye,
  Image, PenTool, Clock, Trash2, Edit3, Calendar,
  Sparkles, ArrowRight, Headphones, Radio, Film,
  MessageSquare, Wand2, ChevronRight, Plus, Folder
} from 'lucide-react';

const QUICK_CREATE = [
  { id: 'upload', label: 'Upload Content', desc: 'Photos, videos, audio', icon: Upload, route: '/feed', color: '#8B5CF6' },
  { id: 'post', label: 'Create Post', desc: 'Share to your feed', icon: PenTool, route: '/feed', color: '#06B6D4' },
  { id: 'reel', label: 'Create Clip', desc: 'Short-form video', icon: Film, route: '/reels', color: '#F97316' },
];

const AI_TOOLS = [
  { id: 'caption', label: 'AI Captions', desc: 'Auto-generate captions', icon: MessageSquare, route: '/viral-engine', color: '#F59E0B' },
  { id: 'idea', label: 'Start from Idea', desc: 'AI generates content', icon: Wand2, route: '/ai-assistant', color: '#10B981' },
];

const CONTENT_TYPES = [
  { id: 'music', label: 'Music', icon: Headphones, route: '/feed' },
  { id: 'video', label: 'Video', icon: Video, route: '/reels' },
  { id: 'podcast', label: 'Podcast', icon: Radio, route: '/feed' },
  { id: 'clips', label: 'Short Clips', icon: Film, route: '/reels' },
];

export default function CreateHubPage() {
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [drafts, setDrafts] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDrafts = async () => {
      if (!isAuthenticated) return;
      setLoading(true);
      try {
        const [draftsRes, scheduledRes] = await Promise.all([
          fetch(`${API}/posts/drafts`, { headers: getAuthHeaders() }).catch(() => null),
          fetch(`${API}/posts/scheduled`, { headers: getAuthHeaders() }).catch(() => null),
        ]);
        if (draftsRes?.ok) setDrafts(await draftsRes.json().then(d => d.drafts || d || []).catch(() => []));
        if (scheduledRes?.ok) setScheduled(await scheduledRes.json().then(d => d.posts || d || []).catch(() => []));
      } catch {} finally {
        setLoading(false);
      }
    };
    fetchDrafts();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="create-hub-page">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 pt-6 pb-4">
        <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Barlow Condensed, system-ui' }}>
          Create
        </h1>
        <p className="text-sm text-muted-foreground/60 mt-1">All your creation tools in one place</p>
      </div>

      <div className="max-w-4xl mx-auto px-4 space-y-8">
        {/* Quick Create */}
        <section data-testid="quick-create-section">
          <h2 className="text-sm font-semibold text-muted-foreground/70 uppercase tracking-wider mb-3">Quick Create</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {QUICK_CREATE.map(item => (
              <button
                key={item.id}
                onClick={() => navigate(item.route)}
                className="group flex items-center gap-3 p-4 rounded-xl border border-border bg-muted/20 hover:border-border hover:bg-muted/25 transition-all text-left"
                data-testid={`quick-create-${item.id}`}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}15`, border: `1px solid ${item.color}30` }}>
                  <item.icon className="w-5 h-5" style={{ color: item.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{item.label}</p>
                  <p className="text-xs text-muted-foreground/50">{item.desc}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-muted-foreground/50 transition-colors" />
              </button>
            ))}
          </div>
        </section>

        {/* AI Tools */}
        <section data-testid="ai-tools-section">
          <h2 className="text-sm font-semibold text-muted-foreground/70 uppercase tracking-wider mb-3">AI Tools</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {AI_TOOLS.map(tool => (
              <Link
                key={tool.id}
                to={tool.route}
                className="group flex flex-col items-center gap-2 p-4 rounded-xl border border-border bg-muted/20 hover:border-border hover:bg-muted/25 transition-all text-center"
                data-testid={`ai-tool-${tool.id}`}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${tool.color}15`, border: `1px solid ${tool.color}30` }}>
                  <tool.icon className="w-5 h-5" style={{ color: tool.color }} />
                </div>
                <span className="font-medium text-xs">{tool.label}</span>
                <span className="text-[10px] text-muted-foreground/50">{tool.desc}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Drafts & Scheduled */}
        <section data-testid="drafts-section">
          <h2 className="text-sm font-semibold text-muted-foreground/70 uppercase tracking-wider mb-3">
            Drafts & Scheduled
          </h2>
          {drafts.length === 0 && scheduled.length === 0 ? (
            <Card className="p-8 text-center bg-muted/20 border-border">
              <Folder className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground/50">No drafts or scheduled content yet</p>
              <p className="text-xs text-muted-foreground/40 mt-1">Create something and save it as a draft</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {drafts.slice(0, 5).map(d => (
                <div key={d.post_id || d.draft_id} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-muted/20">
                  <Edit3 className="w-4 h-4 text-muted-foreground/40" />
                  <span className="text-sm flex-1 truncate">{d.content || d.title || 'Untitled draft'}</span>
                  <Badge variant="outline" className="text-[10px]">Draft</Badge>
                </div>
              ))}
              {scheduled.slice(0, 5).map(s => (
                <div key={s.post_id || s.schedule_id} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-muted/20">
                  <Calendar className="w-4 h-4 text-amber-400/40" />
                  <span className="text-sm flex-1 truncate">{s.content || s.title || 'Scheduled post'}</span>
                  <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-400">Scheduled</Badge>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Content Types */}
        <section data-testid="content-types-section">
          <h2 className="text-sm font-semibold text-muted-foreground/70 uppercase tracking-wider mb-3">Content Types</h2>
          <div className="grid grid-cols-4 gap-2">
            {CONTENT_TYPES.map(ct => (
              <Link
                key={ct.id}
                to={ct.route}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border bg-muted/20 hover:border-border transition-all"
                data-testid={`content-type-${ct.id}`}
              >
                <ct.icon className="w-6 h-6 text-muted-foreground/50" />
                <span className="text-xs font-medium text-muted-foreground/70">{ct.label}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
